<template>
  <div
    v-if="showModal"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    style="background: rgba(0, 0, 0, 0.7);"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-clipboard-check"></i> 
            Pre-Launch Checklist: {{ experimentName }}
          </h5>
          <button type="button" class="close" @click="closeModal">
            <span>&times;</span>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Experiment Type Selection -->
          <div class="experiment-type-selector mb-4">
            <label class="type-label">Experiment Type:</label>
            <div class="experiment-type-buttons">
              <button 
                type="button"
                class="type-btn"
                :class="{ 'type-btn-active': isMonetization }"
                @click="isMonetization = true"
              >
                <i class="fas fa-dollar-sign"></i> Monetization
              </button>
              <button 
                type="button"
                class="type-btn"
                :class="{ 'type-btn-active': !isMonetization }"
                @click="isMonetization = false"
              >
                <i class="fas fa-flask"></i> Other
              </button>
            </div>
          </div>

          <!-- UI Images Preview Section -->
          <div class="images-preview-section mb-4">
            <h6 class="section-title">
              <i class="fas fa-images"></i> Uploaded UI Images
            </h6>
            <div class="images-grid">
              <div 
                v-for="image in sortedUiImages" 
                :key="image.url"
                class="image-preview-card"
                @click="openLightbox(image.url)"
              >
                <div class="image-variant-label">Variant {{ image.variant }}</div>
                <img :src="image.url" :alt="`Variant ${image.variant}`" class="preview-img" />
                <span class="click-hint"><i class="fas fa-expand"></i> Click to enlarge</span>
              </div>
              <div v-if="uiImages.length === 0" class="no-images-message">
                <i class="fas fa-exclamation-triangle"></i>
                No images uploaded yet
              </div>
            </div>
          </div>

          <!-- Monetization Checklist -->
          <div v-if="isMonetization" class="checklist-section">
            <h6 class="section-title">
              <i class="fas fa-dollar-sign"></i> Monetization Checks
            </h6>
            <div 
              v-for="(item, index) in monetizationChecklist" 
              :key="`mon-${index}`"
              class="checklist-item"
              :class="{ 'checked': item.checked }"
            >
              <label>
                <input type="checkbox" v-model="item.checked" />
                <span class="check-label">{{ item.label }}</span>
              </label>
            </div>
          </div>

          <!-- General Checklist (shown for non-monetization) -->
          <div v-else class="checklist-section">
            <h6 class="section-title">
              <i class="fas fa-tasks"></i> General Checks
            </h6>
            <div 
              v-for="(item, index) in generalChecklist" 
              :key="`gen-${index}`"
              class="checklist-item"
              :class="{ 'checked': item.checked }"
            >
              <label>
                <input type="checkbox" v-model="item.checked" />
                <span class="check-label">{{ item.label }}</span>
              </label>
            </div>
          </div>

          <!-- Final Reminder -->
          <div class="final-reminder mt-4">
            <div class="alert alert-info">
              <i class="fas fa-bell"></i>
              <strong>Remember:</strong> Check the experiment status after 1 hour of launch.
            </div>
          </div>

          <!-- Progress Indicator -->
          <div class="progress-indicator">
            <div class="progress">
              <div 
                class="progress-bar" 
                :class="progressBarClass"
                role="progressbar" 
                :style="{ width: progressPercentage + '%' }"
              >
                {{ checkedCount }}/{{ totalCount }} completed
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-success" 
            :disabled="!allChecked"
            @click="confirmStart"
          >
            <i class="fas fa-rocket"></i> Start Experiment
          </button>
        </div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div v-if="lightboxImage" class="lightbox-overlay" @click="closeLightbox">
      <div class="lightbox-content" @click.stop>
        <button v-show="lightboxLoaded" class="lightbox-close" @click="closeLightbox">
          <i class="fas fa-times"></i>
        </button>
        <img 
          :src="lightboxImage" 
          alt="Full size image" 
          @load="lightboxLoaded = true"
          :class="{ 'lightbox-img-loaded': lightboxLoaded }"
        />
        <div v-if="!lightboxLoaded" class="lightbox-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExperimentStartChecklist',
  props: {
    experimentName: {
      type: String,
      default: ''
    },
    uiImages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showModal: false,
      isMonetization: true,
      lightboxImage: null,
      lightboxLoaded: false,
      monetizationChecklist: [
        { label: 'Are the products listed in catalog manager?', checked: false },
        { label: 'Are the products approved in AppStore and PlayStore?', checked: false },
        { label: 'Is it QA tested?', checked: false },
        { label: 'Is it tested on staging with test groups?', checked: false },
        { label: 'Have you checked IAP catalog in production?', checked: false },
        { label: 'Have you checked the production paywall structure?', checked: false },
        { label: 'Have you checked the pricing tier parameter in production?', checked: false }
      ],
      generalChecklist: [
        { label: 'Is it QA tested?', checked: false },
        { label: 'Are parameters tested on staging?', checked: false }
      ]
    };
  },
  computed: {
    sortedUiImages() {
      if (!this.uiImages || this.uiImages.length === 0) return [];
      return [...this.uiImages].sort((a, b) => {
        return (a.variant || '').localeCompare(b.variant || '');
      });
    },
    currentChecklist() {
      return this.isMonetization ? this.monetizationChecklist : this.generalChecklist;
    },
    allChecked() {
      return this.currentChecklist.every(item => item.checked);
    },
    checkedCount() {
      return this.currentChecklist.filter(item => item.checked).length;
    },
    totalCount() {
      return this.currentChecklist.length;
    },
    progressPercentage() {
      if (this.totalCount === 0) return 0;
      return Math.round((this.checkedCount / this.totalCount) * 100);
    },
    progressBarClass() {
      if (this.progressPercentage === 100) return 'bg-success';
      if (this.progressPercentage >= 50) return 'bg-warning';
      return 'bg-danger';
    }
  },
  methods: {
    openModal() {
      this.showModal = true;
      this.resetChecklist();
    },
    closeModal() {
      this.showModal = false;
      this.$emit('cancel');
    },
    resetChecklist() {
      this.monetizationChecklist.forEach(item => item.checked = false);
      this.generalChecklist.forEach(item => item.checked = false);
      this.isMonetization = true;
    },
    confirmStart() {
      this.showModal = false;
      this.$emit('confirmed');
    },
    openLightbox(imageUrl) {
      this.lightboxLoaded = false;
      this.lightboxImage = imageUrl;
    },
    closeLightbox() {
      this.lightboxImage = null;
    }
  }
};
</script>

<style scoped>
.modal-content {
  background-color: #fff;
  color: #333;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  border-radius: 12px 12px 0 0;
  padding: 1rem 1.5rem;
}

.modal-title {
  color: #2c3e50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-title i {
  color: #3498db;
}

.modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.experiment-type-selector {
  text-align: center;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.type-label {
  font-weight: 600;
  margin-bottom: 12px;
  display: block;
  color: #495057;
  font-size: 15px;
}

.experiment-type-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.type-btn {
  padding: 12px 30px;
  font-weight: 600;
  font-size: 15px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  background-color: #f8f9fa;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-btn:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.type-btn-active {
  background-color: #3498db;
  border-color: #3498db;
  color: #fff;
  box-shadow: 0 3px 10px rgba(52, 152, 219, 0.4);
}

.type-btn-active:hover {
  background-color: #2980b9;
  border-color: #2980b9;
}

.images-preview-section {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.section-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #3498db;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #3498db;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 15px;
}

.image-preview-card {
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 12px;
  background-color: #fff;
  text-align: center;
  transition: all 0.2s ease;
}

.image-preview-card:hover {
  border-color: #3498db;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.15);
  cursor: pointer;
}

.click-hint {
  font-size: 0.7rem;
  color: #95a5a6;
  margin-top: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.image-preview-card:hover .click-hint {
  opacity: 1;
}

.image-variant-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 14px;
  background-color: #e9ecef;
  padding: 5px 10px;
  border-radius: 4px;
}

.preview-img {
  max-width: 100%;
  max-height: 120px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  object-fit: contain;
}

.no-images-message {
  grid-column: 1 / -1;
  text-align: center;
  padding: 30px;
  color: #e74c3c;
  font-weight: 500;
  background-color: #fff;
  border-radius: 8px;
  border: 2px dashed #e74c3c;
}

.no-images-message i {
  margin-right: 8px;
}

.checklist-section {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.checklist-item {
  padding: 12px 15px;
  margin-bottom: 8px;
  background-color: #fff;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.checklist-item:hover {
  border-color: #3498db;
  background-color: #f8f9fa;
}

.checklist-item.checked {
  background-color: #d4edda;
  border-color: #28a745;
}

.checklist-item.checked .check-label {
  color: #155724;
}

.checklist-item label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0;
  width: 100%;
}

.checklist-item input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #28a745;
}

.check-label {
  flex: 1;
  color: #495057;
  font-size: 15px;
  line-height: 1.4;
}

.final-reminder .alert {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
  border-radius: 8px;
}

.final-reminder .alert i {
  font-size: 18px;
}

.progress-indicator {
  margin-top: 20px;
}

.progress {
  height: 25px;
  border-radius: 8px;
  background-color: #e9ecef;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  transition: width 0.3s ease;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-footer .btn {
  padding: 10px 25px;
  font-weight: 500;
  border-radius: 6px;
}

.modal-footer .btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.modal-footer .btn-success:hover:not(:disabled) {
  background-color: #218838;
  border-color: #1e7e34;
}

.modal-footer .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer .btn-success i {
  margin-right: 6px;
}

.close {
  font-size: 1.5rem;
  color: #495057;
  opacity: 0.7;
}

.close:hover {
  opacity: 1;
}

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  cursor: pointer;
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  cursor: default;
}

.lightbox-content img {
  max-width: 100%;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.lightbox-content img.lightbox-img-loaded {
  opacity: 1;
}

.lightbox-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 2.5rem;
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s ease;
  z-index: 1;
}

.lightbox-close:hover {
  color: #e74c3c;
}
</style>
